import React from 'react';
import styled, { css } from 'styled-components';
import { useDisclosure } from './Disclosure';
import { VIEWPORT, LOZENGE_HEIGHT } from '../settings/Global';
import { trackClick } from '../utils/googleEvents';
import {
    type LicenceType,
    type Tier,
    useActiveTieredLicensingsForLicenceType,
} from '../hooks/useActiveLicenceTypes';
import allLicenceSelectorData from '../data/LicenceSelectorDisclosures';
import useCartQuery from '../hooks/useCartQuery';
import LicenceQuantitySelector from './LicenceQuantitySelector';
import useConfig from '../hooks/useConfig';
import { sentryException } from '../utils/sentry';
import { TEST_ID } from '../settings/E2e';
import { useDebouncedCartUpdateMutationContext } from '../hooks/useDebouncedCartUpdateMutation';
import { useErrorOverlayState } from './GlobalRuntimeState';
import { LOZENGE_BORDER_RADIUS } from './Lozenge';
import { invertColorVariables } from '../utils/stylesMixins';
import { FieldType, RadioCheckbox } from './RadioCheckbox';
import { LicenceTypeId } from '../gql/api-public';
import { Button as InlineUnderlinedButton } from './InlineUnderlinedLink';

const COL_COUNT = 6;

const LearnMoreLink = styled(InlineUnderlinedButton)<{
    $isSelected: boolean;
    $isOpen: boolean;
}>`
    --color: var(--foregroundColorMix5);
    --underlineColor: var(--foregroundColorMix4);
    --hoverColor: var(--foregroundColor);
    --hoverUnderlineColor: var(--hoverColor);

    ${({ $isSelected }): ReturnType<typeof css> | null =>
        $isSelected
            ? css`
                  --hoverColor: var(--backgroundColor);
              `
            : null};

    ${({ $isOpen, $isSelected }): ReturnType<typeof css> | null =>
        $isOpen
            ? css`
                  --color: ${$isSelected
                      ? 'var(--backgroundColor)'
                      : 'var(--foregroundColor)'};
              `
            : null};
`;

const Container = styled.div<{ $isSelected: boolean }>`
    border-radius: ${LOZENGE_BORDER_RADIUS}px;
    background-color: var(--menuBackgroundColor);

    --paddingVer: var(--spacing2);
    --paddingHor: var(--spacing3);
    --lozengeHoverColor: var(--backgroundColor);
    --lozengeBackgroundColor: transparent;
    --lozengeBackgroundHoverColor: var(--foregroundColor);
    --lozengeBackgroundActiveColor: var(--lozengeBackgroundHoverColor);

    @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
        --paddingHor: var(--spacing2);
    }

    ${({ $isSelected }): ReturnType<typeof css> =>
        $isSelected
            ? css`
                  background-color: var(--foregroundColor);
                  color: var(--backgroundColor);

                  --lozengeColor: var(--backgroundColor);
                  --lozengeHoverColor: var(--foregroundColor);
                  --lozengeBackgroundColor: var(--foregroundColor);
                  --lozengeBackgroundHoverColor: var(--backgroundColor);
                  --lozengeBackgroundActiveColor: var(
                      --lozengeBackgroundHoverColor
                  );
              `
            : css`
                  &:hover {
                      background-color: var(--foregroundColorMix7_5);
                  }
              `}
`;

const Label = styled.label`
    cursor: pointer;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: baseline;
    padding: var(--paddingVer) var(--paddingHor);
    column-gap: calc(var(--spacingCheckable) * 2);
    font-size: var(--fontSizeFixed2);
    line-height: var(--lineHeightBody2);

    /*
    Extra bottom padding to compensate for lozenge-style select lists at the top,
    which have extra height.
    */
    padding-bottom: calc(var(--paddingVer) + ((${LOZENGE_HEIGHT}px - 1lh) / 2));
`;

const Inner = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    column-gap: var(--gridColumnGap);

    @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
        display: grid;
        grid-template-columns: 1fr auto;
    }
`;

const Title = styled.span`
    font-weight: bold;
`;

const Description = styled.div`
    color: var(--foregroundColorMix5);
    flex-basis: 100%;

    @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
        grid-column: 1 / -1;
    }
`;

const LearnMorePanel = styled.div<{ $isSelected: boolean }>`
    ${({ $isSelected }): ReturnType<typeof css> | null =>
        $isSelected ? invertColorVariables : null};

    color: var(--foregroundColor);
    background-color: var(--foregroundColorMix7);
    padding: var(--paddingHor) 0;

    @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
        padding: var(--paddingHor);
    }

    --border: 1px solid var(--foregroundColorMix6);

    dl {
        display: grid;
        grid-template-columns: repeat(${COL_COUNT}, 1fr);
        grid-gap: var(--spacing2) var(--gridColumnGap);

        @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
            grid-template-columns: 100%;
        }
    }

    hr {
        grid-column: 1 / span ${COL_COUNT};
        border: none;
        border-top: var(--border);
        margin: 0 var(--paddingHor);

        @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
            grid-column: initial;
            margin: 0;
        }
    }

    dt {
        color: var(--foregroundColorMix4);
        grid-column: 1 / span 2;
        padding-left: var(--paddingHor);

        @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
            grid-column: initial;
            padding-left: 0;
        }
    }

    dd {
        grid-column: 3 / span 4;
        padding-right: var(--paddingHor);

        @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
            grid-column: initial;
            padding-right: 0;
        }
    }

    p + p {
        margin-top: var(--spacing1);
    }

    b {
        font-weight: bold;
    }
`;

function LicenceSelectorOption({
    licenceType,
}: {
    licenceType: LicenceType;
}): React.ReactElement {
    const config = useConfig();
    const title = licenceType.name;
    const cartQuery = useCartQuery();
    const cart = cartQuery?.data?.cart;
    const doDebouncedCartUpdate = useDebouncedCartUpdateMutationContext();
    const tieredLicences = useActiveTieredLicensingsForLicenceType(licenceType);
    const [, setShowErrorOverlay] = useErrorOverlayState();
    const [isSelected, setIsSelected] = React.useState<boolean>(false);

    const selectedTier = React.useMemo(() => {
        const cartTier = cart?.licenceTiers.find(
            (cartTier) => cartTier.tier.licenceType.id === licenceType.id,
        )?.tier;

        setIsSelected(Boolean(cartTier));

        if (!cartTier) {
            return tieredLicences[0].tiers[0];
        }
        const tieredLicence = tieredLicences.find(
            (tl) => tl.id === cartTier.tieredLicence.id,
        );
        if (!tieredLicence) {
            throw Error(
                `Couldn't retrieve tieredLicence for ${cartTier.tieredLicence.id}`,
            );
        }
        const tier = tieredLicence.tiers.find((t) => t.id === cartTier.id);
        if (!tier) {
            throw Error(`Couldn't retrieve tier for ${cartTier.id}`);
        }
        return tier;
    }, [cart?.licenceTiers]);

    const eventContentType = 'licence_selector_disclosure';
    const disclosure = useDisclosure({
        onExpand: () =>
            trackClick(
                eventContentType,
                `${eventContentType}_${
                    title ? title.toString().toLowerCase() : ''
                }`,
            ),
    });

    const inputId = `checkbox-${licenceType.id}`;
    const licenceSelectorData =
        allLicenceSelectorData[licenceType.id as LicenceTypeId];

    return (
        <Container $isSelected={isSelected}>
            <Label
                htmlFor={inputId}
                data-cy={TEST_ID.LICENCE_SELECTOR_LABEL}
                data-cy-licence={licenceType.id}
            >
                <RadioCheckbox
                    name="licenceType"
                    fieldType={FieldType.CHECKBOX}
                    checked={isSelected}
                    id={inputId}
                    /* Ensure the Advertising modal shows and isn't automatically dismissed */
                    onClick={(e): void => e.stopPropagation()}
                    onChange={(e): void => {
                        if (!cart) {
                            return;
                        }

                        const tierIdToRemove = !e.target.checked
                            ? selectedTier.id
                            : undefined;

                        const tierToAdd = e.target.checked
                            ? selectedTier
                            : undefined;

                        doDebouncedCartUpdate({
                            cart,
                            config,
                            tierIdToRemove,
                            tierToAdd,
                        }).catch((error) => {
                            sentryException(error);
                            setShowErrorOverlay({
                                isShown: true,
                            });
                        });
                    }}
                />
                <Inner>
                    <Title data-cy={TEST_ID.LICENCE_SELECTOR_TITLE}>
                        {title}
                    </Title>
                    <LicenceQuantitySelector
                        licenceType={licenceType}
                        onSelectOpenChange={(open: boolean, tier): void => {
                            if (isSelected || !open || !cart) {
                                return;
                            }
                            // Select the default licence when opening a select list,
                            // if there is no licence selected... In an effort to help
                            // customers understand that they need to select an entire
                            // licence block.
                            doDebouncedCartUpdate({
                                cart,
                                config,
                                tierToAdd: tier,
                            }).catch((error) => {
                                sentryException(error);
                                setShowErrorOverlay({
                                    isShown: true,
                                });
                            });
                        }}
                        onChange={(tierToAdd: Tier): void => {
                            if (!cart) {
                                return;
                            }
                            doDebouncedCartUpdate({
                                cart,
                                config,
                                tierToAdd,
                            }).catch((error) => {
                                sentryException(error);
                                setShowErrorOverlay({
                                    isShown: true,
                                });
                            });
                        }}
                        invertContentColors
                    />
                    {licenceSelectorData ? (
                        <Description>
                            {licenceSelectorData.description}.{' '}
                            <LearnMoreLink
                                {...disclosure.buttonAttributes}
                                $isSelected={isSelected}
                                $isOpen={!disclosure.panelAttributes.hidden}
                            >
                                {disclosure.panelAttributes.hidden
                                    ? `Learn more`
                                    : `Close`}
                            </LearnMoreLink>
                        </Description>
                    ) : null}
                </Inner>
            </Label>
            {licenceSelectorData ? (
                <LearnMorePanel
                    {...disclosure.panelAttributes}
                    $isSelected={isSelected}
                >
                    {licenceSelectorData.moreInfo}
                </LearnMorePanel>
            ) : null}
        </Container>
    );
}

export default LicenceSelectorOption;
